import React, { useEffect, useRef } from 'react';
import { Menu, type MenuItem } from '@allygpo/ally-ux';
import type { SubNavItem } from './navItems';

interface NavFlyoutProps {
  readonly id: string;
  readonly section: string;
  readonly items: readonly SubNavItem[];
  readonly activePath: string;
  /** Viewport position of the rail button it belongs to. */
  readonly anchor: { readonly top: number; readonly left: number };
  /** Move focus to the first item on open (opened from the keyboard). */
  readonly focusFirst: boolean;
  readonly onNavigate: (path: string) => void;
  /** `returnFocus`: hand focus back to the rail button (Escape). */
  readonly onClose: (returnFocus: boolean) => void;
  readonly onPointerEnter: () => void;
  readonly onPointerLeave: () => void;
}

const ItemIcon: React.FC<{ path: string }> = ({ path }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={path} /></svg>
);

/**
 * AllyIQ-style pop-out beside a rail button, listing the section's sub-pages: Ally UX's Menu (inline), in a
 * wrapper that places it next to the rail. Menu handles the arrow, Home/End and Enter keys; the wrapper closes
 * it on Escape, Left arrow and Tab.
 */
const NavFlyout = ({ id, section, items, activePath, anchor, focusFirst, onNavigate, onClose, onPointerEnter, onPointerLeave }: NavFlyoutProps): React.JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Focusing the menu list moves Menu's own focus to its first item.
  useEffect(() => { if (focusFirst) wrapperRef.current?.querySelector<HTMLElement>('[role="menu"]')?.focus(); }, [focusFirst]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      event.preventDefault();
      onClose(true);
    } else if (event.key === 'Tab') {
      onClose(false);
    }
  };

  // Grouped under the section's name, as AllyIQ's rail panels carry a title.
  const model: MenuItem[] = [{
    label: section,
    items: items.map((item) => ({
      label: item.label,
      icon: <ItemIcon path={item.path} />,
      className: item.to === activePath ? 'bg-surface-selected' : undefined,
      command: () => onNavigate(item.to),
    })),
  }];

  return <div ref={wrapperRef} id={id} className="siq-flyout" style={{ top: anchor.top, left: anchor.left }}
    onMouseEnter={onPointerEnter} onMouseLeave={onPointerLeave} onKeyDown={onKeyDown}>
    {/* A fixed id: without one, Menu's item ids are still empty when it takes focus, so Enter does nothing. */}
    <Menu id={`${id}-menu`} model={model} aria-label={section} />
  </div>;
};

export default NavFlyout;
